import { useCallback, useMemo, useRef } from "react";
import { formatValue, asNumber } from "../../lib/kernelFormat.js";
import { CanvasSurface, KernelEmpty, resolveCssColor, lerpColor, fillStippleRect } from "./shared.jsx";

export const kind = "treemap";

const MONO_FONT = "ui-monospace, SFMono-Regular, Menlo, monospace";

// Squarified treemap. Sorts cells descending by value, then walks the
// list packing into the longer axis until aspect ratio worsens.
// Classic Bruls/Huijsen/van Wijk algorithm, ~80 LOC.
export function Component({ spec, data, fillHeight = false }) {
    const items = useMemo(() => buildItems(spec, data), [spec, data]);
    const height = Math.max(140, Math.min(500, Number(spec.height) || 240));
    const color = spec.color || "var(--green)";
    // Laid-out cell rects from the last draw, in canvas CSS pixels — the
    // hover hit-test reads these so tooltip geometry always matches what's
    // actually painted.
    const rectsRef = useRef([]);

    const draw = useCallback((ctx, { width, height: h, root }) => {
        rectsRef.current = [];
        if (!items.length) return;
        const fg = resolveCssColor(root, color) || "#5a8c5e";
        const bg = resolveCssColor(root, "var(--plot-bg)") || "#1a1a1a";
        const ink = resolveCssColor(root, "var(--ink)") || "#fff";
        // Cell text sits on a dot-free knockout plate (below) so it never
        // fights the stipple: the value is the hero in --ink, the label a
        // smaller --muted eyebrow above it. (--plot-ink-soft, a ~17% gridline
        // tint, washed the value out to nothing over a dense cell.)
        const muted = resolveCssColor(root, "var(--muted)") || "#888";

        const total = items.reduce((s, it) => s + it.value, 0);
        if (total <= 0) return;

        const rects = squarify(items.map((it) => ({ ...it, weight: (it.value / total) * width * h })), {
            x: 0, y: 0, w: width, h,
        });
        rectsRef.current = rects;
        const max = items[0].value;

        for (const r of rects) {
            const t = max > 0 ? r.value / max : 0;
            // faint lerp wash to give each cell a readable base + stipple
            // overlay so the texture matches SurfaceChart.
            const wash = lerpColor(bg, fg, 0.08 + t * 0.3);
            ctx.fillStyle = wash;
            ctx.fillRect(r.x, r.y, r.w, r.h);
            fillStippleRect(ctx, r.x, r.y, r.w, r.h, fg, { density: 0.25 + t * 0.75 });
            ctx.strokeStyle = bg;
            ctx.lineWidth = 1;
            ctx.strokeRect(r.x + 0.5, r.y + 0.5, r.w - 1, r.h - 1);

            // Label + value on a dot-free knockout so the stipple can't erode
            // the glyphs. The value is the hero (large, --ink); the label is a
            // compact uppercase eyebrow (--muted) above it. The knockout is the
            // cell's OWN base wash (repainted over the dots, no color shift) —
            // it just pauses the texture behind the text, so there's no visible
            // box, only a calm patch of the same cell. Short cells drop to
            // label-only so a two-line knockout never spills past the cell into
            // its neighbour below.
            r.valueShown = false;
            if (r.w > 52 && r.h > 22) {
                ctx.textBaseline = "top";
                ctx.textAlign = "left";
                const big = r.h > 40 && r.w > 68;
                const labelFs = 8;
                const valueFs = big ? Math.min(15, Math.max(11, r.w / 11)) : 10;
                const padX = 5, padY = 4, gap = big ? 3 : 2;
                const innerW = r.w - 8 - padX * 2;

                ctx.font = `600 ${labelFs}px ${MONO_FONT}`;
                const label = truncateToFit(ctx, String(r.label).toUpperCase(), innerW);
                const labelW = ctx.measureText(label).width;

                const fullH = padY * 2 + labelFs + gap + valueFs;
                const showValue = r.h - 8 >= fullH;
                let valText = "", valW = 0;
                if (showValue) {
                    const rawVal = spec.format ? formatValue(r.value, spec.format) : formatValue(r.value, "number");
                    ctx.font = `600 ${valueFs}px ${MONO_FONT}`;
                    valText = truncateToFit(ctx, rawVal, innerW);
                    valW = ctx.measureText(valText).width;
                    r.valueShown = valText === rawVal;
                }

                const plateW = Math.min(r.w - 8, Math.max(labelW, valW) + padX * 2);
                const plateH = showValue ? fullH : padY * 2 + labelFs;
                ctx.fillStyle = wash;
                roundRect(ctx, r.x + 4, r.y + 4, plateW, plateH, 3);
                ctx.fill();

                ctx.fillStyle = muted;
                ctx.font = `600 ${labelFs}px ${MONO_FONT}`;
                ctx.fillText(label, r.x + 4 + padX, r.y + 4 + padY);
                if (showValue) {
                    ctx.fillStyle = ink;
                    ctx.font = `600 ${valueFs}px ${MONO_FONT}`;
                    ctx.fillText(valText, r.x + 4 + padX, r.y + 4 + padY + labelFs + gap);
                }
            }
        }
    }, [items, color, spec.format]);

    // Hover tooltip for cells whose count isn't fully readable: tiny cells
    // draw no text at all, short ones drop the value line, narrow ones elide
    // it. Cells already showing their full value get none — it would only
    // repeat the plate. Content and placement mirror the drawn label plates
    // (uppercase eyebrow over the value, anchored at the cell's top-left).
    const tooltipFn = useCallback((x, y) => {
        const idx = rectsRef.current.findIndex(
            (it) => x >= it.x && x < it.x + it.w && y >= it.y && y < it.y + it.h,
        );
        if (idx < 0) return null;
        const r = rectsRef.current[idx];
        if (r.valueShown) return null;
        return {
            key: `cell:${idx}`,
            rect: { x: r.x, y: r.y, w: r.w, h: r.h },
            content: (
                <div className="font-mono leading-[1.35]">
                    <div className="text-[9px] font-semibold uppercase text-[color:var(--muted)]">
                        {r.label}
                    </div>
                    <div className="text-[12px] font-semibold text-[color:var(--ink)]">
                        {formatValue(r.value, spec.format || "number")}
                    </div>
                </div>
            ),
        };
    }, [spec.format]);

    if (!items.length) return <KernelEmpty message={spec.empty || "no rows"} />;

    return (
        <div className={`rounded-[2px] border border-[color:var(--line)] bg-[color:var(--panel-soft)] px-[10px] py-[8px]${fillHeight ? " flex h-full min-h-0 flex-col" : ""}`}>
            {spec.title ? (
                <div className="mb-[6px] text-[10px] uppercase tracking-[0.14em] text-[color:var(--muted)]">
                    {spec.title}
                </div>
            ) : null}
            <CanvasSurface
                draw={draw}
                deps={[items, color, spec.format]}
                height={fillHeight ? null : height}
                className={`w-full bg-[color:var(--plot-bg)] rounded-[2px] border border-[color:var(--line-soft)]${fillHeight ? " flex-1 min-h-0" : ""}`}
                tooltipFn={tooltipFn}
            />
        </div>
    );
}

function buildItems(spec, data) {
    const arr = Array.isArray(data) ? data : [];
    const items = arr
        .map((row) => ({
            label: String(row?.[spec.labelField] ?? ""),
            value: asNumber(row?.[spec.valueField]) ?? 0,
        }))
        .filter((it) => it.value > 0)
        .sort((a, b) => b.value - a.value);
    const cap = Math.max(1, spec.maxItems || 24);
    if (items.length > cap) {
        const head = items.slice(0, cap - 1);
        const tail = items.slice(cap - 1);
        const restValue = tail.reduce((s, it) => s + it.value, 0);
        if (restValue > 0) head.push({ label: `+${tail.length} more`, value: restValue });
        return head;
    }
    return items;
}

// Squarified treemap layout. Returns [{label, value, x, y, w, h}, ...].
function squarify(items, bounds) {
    const out = [];
    const stack = [{ items: items.slice(), bounds: { ...bounds } }];
    while (stack.length) {
        const { items: list, bounds: b } = stack.pop();
        if (!list.length) continue;
        const totalW = list.reduce((s, it) => s + it.weight, 0);
        if (totalW <= 0 || b.w <= 0 || b.h <= 0) continue;

        const along = b.w >= b.h ? "h" : "v"; // pack into the shorter side
        const sideLen = along === "h" ? b.h : b.w;

        let row = [];
        let rowSum = 0;
        let bestAspect = Infinity;
        let i = 0;
        for (; i < list.length; i++) {
            const candidate = [...row, list[i]];
            const candSum = rowSum + list[i].weight;
            const aspect = worstAspect(candidate, candSum, sideLen);
            if (aspect <= bestAspect) {
                row = candidate;
                rowSum = candSum;
                bestAspect = aspect;
            } else {
                break;
            }
        }

        // place row
        const breadth = rowSum / (along === "h" ? b.h : b.w);
        let cursor = along === "h" ? b.y : b.x;
        for (const it of row) {
            const len = (it.weight / rowSum) * sideLen;
            const rect = along === "h"
                ? { x: b.x, y: cursor, w: breadth, h: len }
                : { x: cursor, y: b.y, w: len, h: breadth };
            out.push({ ...it, ...rect });
            cursor += len;
        }

        // remaining region
        const rest = list.slice(i);
        const newBounds = along === "h"
            ? { x: b.x + breadth, y: b.y, w: b.w - breadth, h: b.h }
            : { x: b.x, y: b.y + breadth, w: b.w, h: b.h - breadth };
        if (rest.length && newBounds.w > 0 && newBounds.h > 0) {
            stack.push({ items: rest, bounds: newBounds });
        }
    }
    return out;
}

function worstAspect(row, sum, sideLen) {
    if (!row.length || sum <= 0 || sideLen <= 0) return Infinity;
    let rmin = Infinity, rmax = -Infinity;
    for (const it of row) {
        if (it.weight < rmin) rmin = it.weight;
        if (it.weight > rmax) rmax = it.weight;
    }
    const s2 = sum * sum;
    const l2 = sideLen * sideLen;
    return Math.max((l2 * rmax) / s2, s2 / (l2 * rmin));
}

// Trace a rounded rectangle path (caller fills/strokes). ctx.roundRect isn't
// universal enough to rely on, and this keeps the radius clamped to the box.
function roundRect(ctx, x, y, w, h, r) {
    r = Math.min(r, w / 2, h / 2);
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.arcTo(x + w, y, x + w, y + h, r);
    ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r);
    ctx.arcTo(x, y, x + w, y, r);
    ctx.closePath();
}

function truncateToFit(ctx, text, maxWidth) {
    if (ctx.measureText(text).width <= maxWidth) return text;
    let lo = 0, hi = text.length;
    while (lo < hi) {
        const mid = (lo + hi + 1) >> 1;
        if (ctx.measureText(text.slice(0, mid) + "…").width <= maxWidth) lo = mid;
        else hi = mid - 1;
    }
    return text.slice(0, lo) + "…";
}
